import GameObject from '../../GameObject/GameObject';
import AnimationComponent from '../../Component/Animation';
import System from '../System';
import AnimationClip, { AnimationClipEvent } from './AnimationClip';
import Game from '../../Game';
import { StartAnimation, StartAnimationClip } from './AnimationHelper';
import { SceneEvent } from '../../Scene/Scene';
import { IGameState } from '../../State/Game/IGameState';
import DragonbonesComponent from '../../Component/Dragonbones';
import { DragonbonesDefault } from './Clip';

export enum AnimationSystemEvent {
    PLAY = 'play_animation',
    COMPLETE = 'complete_animation',
    STOP = 'stop_animation',
}

export default class AnimationSystem extends System {
    public animationClips: AnimationClip[];

    public init() {
        this.animationClips = [];

        this.gameObjectAdded = this.gameObjectAdded.bind(this);
        this.gameObjectRemoved = this.gameObjectRemoved.bind(this);
        this.gameObjectPlay = this.gameObjectPlay.bind(this);
        this.gameObjectStop = this.gameObjectStop.bind(this);

        this.scene.on(SceneEvent.GameObjectAdded, this.gameObjectAdded);
        this.scene.on(SceneEvent.GameObjectRemoved, this.gameObjectRemoved);
        this.scene.on(AnimationSystemEvent.PLAY, this.gameObjectPlay);
        this.scene.on(AnimationSystemEvent.STOP, this.gameObjectStop);
    }

    private gameObjectPlay(clip: AnimationClip) {
        if (!clip.gameObject.hasComponent(AnimationComponent)) {
            clip.gameObject.addComponent(AnimationComponent);
        }

        // In case animation triggers another animation make sure to push event to next frame.
        Game.ticker.setImmediate(this.playAnimation.bind(this, clip));

        // Set isPlaying in the current frame in case of shenanigans:
        // Card stack visibility is checked every frame and using
        // setImmediate above allows for a single frame before playAnimation
        // is called - therefore causing a flicker.

        // TODO: This may no longer be required.
        //clip.gameObject.getComponent(AnimationComponent).isPlaying = true;
    }

    private gameObjectStop(gameObject: GameObject) {
        const clip = this.findAnimationClip(gameObject);
        if (clip) {
            this.stopAnimation(clip);
        }
    }

    private gameObjectAdded(gameObject: GameObject): void {
        // Check regular animations

        const animationComponent = gameObject.getComponent(AnimationComponent);
        if (animationComponent?.playOnStart && animationComponent?.clipName) {
            StartAnimation(
                gameObject,
                animationComponent.clipName,
                animationComponent.meta
            );
        }

        // Check dragonbones animations

        const dragonbonesComponent = gameObject.getComponent(
            DragonbonesComponent
        );
        if (
            dragonbonesComponent?.animationName &&
            dragonbonesComponent?.playOnStart
        ) {
            StartAnimationClip(
                new DragonbonesDefault(gameObject, {
                    animationName: dragonbonesComponent.animationName,
                    progress: dragonbonesComponent.progress,
                    boneMasks: dragonbonesComponent.boneMasks,
                    playTimes: dragonbonesComponent.playTimes,
                })
            );
        }
    }

    private gameObjectRemoved(gameObject: GameObject<PIXI.Container>): void {
        const clip = this.findAnimationClip(gameObject);
        if (clip) {
            this.stopAnimation(clip);
        }
    }

    public onUpdate(): void {
        // TODO: Change this to animate tweens individually per their animationClip's via the update() method.
        // Currently does not correctly interpret the 'elapsedTime' from the ticker - need to investigate further.

        // Reverse loop to account for splicing.
        let i = this.animationClips.length;
        while (i--) {
            if (this.animationClips[i]) {
                this.animationClips[i].update();
            } else {
                console.warn('Animation clip is undefined');
            }
        }
    }

    public stopAnimation(clip: AnimationClip) {
        if (clip.gameObject) {
            clip.gameObject.getComponent(AnimationComponent).isPlaying = false;
        }
        clip.destroy();
        this.removeAnimationClip(clip);
    }

    public playAnimation(clip: AnimationClip) {
        const currentAnimationClip = this.findAnimationClip(clip.gameObject);

        if (currentAnimationClip && (!clip.additive && !currentAnimationClip.additive)) {
            this.stopAnimation(currentAnimationClip);
        }

        if (!clip.gameObject || clip.gameObject.destroyed) return;

        clip.gameObject.getComponent(AnimationComponent).isPlaying = true;
        clip.gameObject.getComponent(AnimationComponent).clipName =
            clip.constructor.name;
        this.addAnimationClip(clip);

        clip.on(AnimationClipEvent.COMPLETE, this.completeAnimation.bind(this));
        clip.start();
    }

    public completeAnimation(clip: AnimationClip) {
        this.removeAnimationClip(clip);

        const remainingClips = this.findAnimationClip(clip.gameObject);
        if (!remainingClips) {
            const animation = clip.gameObject.getComponent(AnimationComponent);

            if (animation) {
                animation.clipName = undefined;
                animation.isPlaying = false;
            }
        }

        clip.destroy();
    }

    private findAnimationClip(gameObject: GameObject): AnimationClip {
        const clip: AnimationClip = this.animationClips.find(
            (c: AnimationClip) => {
                return c.gameObject === gameObject;
            }
        );

        return clip;
    }

    private addAnimationClip(clip: AnimationClip) {
        const index = this.animationClips.indexOf(clip);
        if (index !== -1) return;
        this.animationClips.push(clip);
    }

    private removeAnimationClip(clip: AnimationClip) {
        const index = this.animationClips.indexOf(clip);
        if (index >= 0) this.animationClips.splice(index, 1);
    }

    public filterGameObject(gameObject: GameObject): boolean {
        return gameObject.hasComponents([AnimationComponent]);
    }

    public onStart(): void {}

    public onStop(): void {}

    public onDestroy(): void {
        this.scene.off(SceneEvent.GameObjectAdded, this.gameObjectAdded);
        this.scene.off(SceneEvent.GameObjectRemoved, this.gameObjectRemoved);
        this.scene.off(AnimationSystemEvent.PLAY, this.gameObjectPlay);
        this.scene.off(AnimationSystemEvent.STOP, this.gameObjectStop);

        this.animationClips.forEach(clip => clip.destroy());
        this.animationClips = [];
    }

    public handleStateChanged(state: IGameState): void {}

    protected onLateUpdate(): void {}

    public async preload(): Promise<any> {
        return undefined;
    }
}
